import React, { useId, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  CheckCircleFill,
  CloudArrowUp,
  ExclamationCircleFill,
  FileEarmark,
  Paperclip,
  PlusCircle,
  X
} from 'react-bootstrap-icons';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import '../styles.scss';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

const ACCEPT_ATTR = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.jpg,.jpeg,.png,.gif,.webp,image/*';

const ACCEPTED_EXTENSIONS = new Set([
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.jpg',
  '.jpeg',
  '.png'
]);

async function uploadFileToGoogleDrive(file, folderId, subFolderName) {
  const formData = new FormData();
  formData.append('name', file.name);
  formData.append('mimeType', file.type);
  formData.append('folderId', folderId);
  if (subFolderName) {
    formData.append('subFolderName', subFolderName);
  }
  formData.append('file', file);

  const { response, error } = await makeRESTApiRequest({
    endpoint: ENDPOINTS.GOOGLE_DRIVE_UPLOAD,
    method: 'POST',
    contentType: 'multipart/form-data',
    requestBody: formData,
    stringify: false
  });

  if (error) {
    throw new Error(error);
  }

  return response;
}

/** Matches Service.supportingDocuments schema (serviceModel.js). */
export const buildSupportingDocument = ({ file, driveResponse, orderType }) => ({
  id: driveResponse?.id || driveResponse?.fileId,
  filename: driveResponse?.name || file.name,
  size: file.size,
  type: driveResponse?.mimeType || file.type || 'application/octet-stream',
  uploadedAt: new Date().toISOString(),
  orderType
});

export const getSupportingDocumentKey = doc => doc._localId || doc.id;

export const getSupportingDocumentName = doc => doc.filename || doc.file?.name || '';

export const getSupportingDocumentSize = doc => doc.size ?? doc.file?.size ?? 0;

/** Persisted records only (excludes pending/error/local File picks). */
export const getPersistedSupportingDocuments = documents => (documents || []).filter(doc => doc.id && !doc.status);

/** @returns {'uploaded' | 'pending' | 'error' | 'selected'} */
export const getDocumentUploadState = doc => {
  if (doc.status === 'pending') return 'pending';
  if (doc.status === 'error') return 'error';
  if (doc.id && !doc.file) return 'uploaded';
  return 'selected';
};

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  const mb = bytes / (1024 * 1024);
  return mb >= 10 ? `${Math.round(mb)}MB` : `${mb.toFixed(1)}MB`;
};

const getFileExtension = name => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
};

const isAcceptedFile = file => {
  const ext = getFileExtension(file.name);
  if (ACCEPTED_EXTENSIONS.has(ext)) return true;
  if (file.type?.startsWith('image/')) return true;
  return false;
};

const toFileArray = fileList => (fileList?.length ? Array.from(fileList) : []);

const AttachSupportingDocuments = ({
  documents,
  onDocumentsChange,
  disabled = false,
  maxFileSize = MAX_FILE_SIZE,
  variant = 'default',
  folderId = null,
  subFolderName,
  orderType,
  onFileSave,
  onBatchUploadComplete,
  syncOnEachUpload = true,
  documentsListFilter
}) => {
  const isCompact = variant === 'compact';
  const isToolbar = variant === 'toolbar';
  const useDriveUpload = Boolean(folderId && (isCompact || isToolbar));
  const fileInputId = useId();
  const [isDragOver, setIsDragOver] = useState(false);
  const [internalDocuments, setInternalDocuments] = useState([]);

  const uploadedDocuments = documents ?? internalDocuments;

  const updateDocuments = updater => {
    const resolveNext = prev => {
      const current = Array.isArray(prev) ? prev : [];
      return typeof updater === 'function' ? updater(current) : updater;
    };

    if (onDocumentsChange) {
      // Parent must accept a setState-style updater (useState setter or wrapper).
      onDocumentsChange(resolveNext);
    } else {
      setInternalDocuments(resolveNext);
    }
  };

  const validateIncomingFiles = fileList => {
    const incoming = toFileArray(fileList);
    const accepted = [];
    const rejected = [];

    incoming.forEach(file => {
      if (file.size > maxFileSize) {
        rejected.push(`${file.name} (exceeds size limit)`);
        return;
      }
      if (!isAcceptedFile(file)) {
        rejected.push(file.name);
        return;
      }
      accepted.push({ _localId: crypto.randomUUID(), file });
    });

    if (rejected.length) {
      toast.error(
        rejected.length === 1
          ? `"${rejected[0]}" is not a supported format or is too large.`
          : `${rejected.length} file(s) were skipped (unsupported format or too large).`
      );
    }

    return accepted;
  };

  const addFiles = fileList => {
    if (disabled) return;

    const accepted = validateIncomingFiles(fileList);
    if (accepted.length) {
      updateDocuments(prev => [...prev, ...accepted]);
    }
  };

  const uploadFilesToGoogleDrive = async fileList => {
    if (!folderId) {
      toast.warning('Folder ID is required');
      return;
    }

    if (disabled) return;

    const accepted = validateIncomingFiles(fileList);
    if (!accepted.length) return;

    const pendingDocuments = accepted.map(({ _localId, file }) => ({
      _localId,
      file,
      status: 'pending'
    }));

    updateDocuments(prev => [...prev, ...pendingDocuments]);

    const results = await Promise.all(
      pendingDocuments.map(async ({ _localId, file }) => {
        try {
          const driveResponse = await uploadFileToGoogleDrive(file, folderId, subFolderName);
          const completedDocument = buildSupportingDocument({
            file,
            driveResponse,
            orderType
          });

          updateDocuments(prev => prev.map(doc => (doc._localId === _localId ? completedDocument : doc)));

          if (syncOnEachUpload) {
            onFileSave?.(completedDocument);
          }

          return { completedDocument };
        } catch (error) {
          updateDocuments(prev => prev.map(doc => (doc._localId === _localId ? { ...doc, status: 'error' } : doc)));
          toast.error(error?.message || 'Upload failed');
          return { error };
        }
      })
    );

    const completedDocuments = results
      .filter(result => result?.completedDocument)
      .map(result => result.completedDocument);

    if (!syncOnEachUpload && completedDocuments.length) {
      onBatchUploadComplete?.(completedDocuments);
    }
  };

  const handleIncomingFiles = fileList => {
    if (useDriveUpload) {
      uploadFilesToGoogleDrive(fileList);
    } else {
      addFiles(fileList);
    }
  };

  const handleFileInputChange = e => {
    e.stopPropagation();
    const selectedFiles = toFileArray(e.target.files);
    e.target.value = '';
    handleIncomingFiles(selectedFiles);
  };

  const handleRemove = (e, docKey) => {
    e.stopPropagation();
    updateDocuments(prev => prev.filter(doc => getSupportingDocumentKey(doc) !== docKey));
  };

  const rootClassName = `attach-supporting-documents${isCompact ? ' attach-supporting-documents--compact' : ''}${
    isToolbar ? ' attach-supporting-documents--toolbar' : ''
  }`;

  const displayDocuments = documentsListFilter ? uploadedDocuments.filter(documentsListFilter) : uploadedDocuments;

  const selectedFilesList =
    displayDocuments.length > 0 ? (
      <div className="attach-supporting-documents__files">
        {!isToolbar && (
          <p className="mb-1 small text-muted font-weight-medium">Selected files ({displayDocuments.length})</p>
        )}
        <div className="attach-supporting-documents__chips">
          {displayDocuments.map(doc => {
            const docKey = getSupportingDocumentKey(doc);
            const name = getSupportingDocumentName(doc);
            const uploadState = getDocumentUploadState(doc);
            const isUploaded = uploadState === 'uploaded';

            return (
              <div
                key={docKey}
                className={`attach-supporting-documents__chip${
                  isUploaded ? ' attach-supporting-documents__chip--uploaded' : ''
                }${uploadState === 'error' ? ' attach-supporting-documents__chip--error' : ''}`}
                title={name}
              >
                <FileEarmark size={15} className="attach-supporting-documents__chip-icon flex-shrink-0" />
                <span className="attach-supporting-documents__chip-name">{name}</span>
                <span className="attach-supporting-documents__chip-size">
                  {formatFileSize(getSupportingDocumentSize(doc))}
                </span>
                {uploadState === 'pending' && (
                  <Spinner size="sm" animation="border" className="attach-supporting-documents__chip-spinner" />
                )}
                {uploadState === 'uploaded' && (
                  <CheckCircleFill size={16} className="text-success flex-shrink-0" aria-label="Uploaded" />
                )}
                {uploadState === 'error' && (
                  <ExclamationCircleFill size={14} className="text-danger flex-shrink-0" aria-label="Upload failed" />
                )}
                {!disabled && uploadState !== 'pending' && (
                  <button
                    type="button"
                    className="attach-supporting-documents__chip-remove"
                    onClick={e => handleRemove(e, docKey)}
                    aria-label={`Remove ${name}`}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    ) : null;

  if (isToolbar) {
    return (
      <div className={rootClassName}>
        <input
          id={fileInputId}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          onChange={handleFileInputChange}
          className="attach-supporting-documents__input"
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
        />
        <label
          htmlFor={fileInputId}
          className={`btn btn-outline-info btn-sm mb-0 d-inline-flex align-items-center${disabled ? ' disabled' : ''}`}
        >
          <PlusCircle size={16} className="mr-1" aria-hidden />
          Add
        </label>
        {selectedFilesList}
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      <input
        id={fileInputId}
        type="file"
        multiple
        accept={ACCEPT_ATTR}
        onChange={handleFileInputChange}
        className="attach-supporting-documents__input"
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />

      {isCompact && selectedFilesList}

      <div
        className={`${isCompact ? 'mt-2' : 'mt-1'} attach-supporting-documents__dropzone${
          isDragOver ? ' attach-supporting-documents__dropzone--active' : ''
        }${disabled ? ' attach-supporting-documents__dropzone--disabled' : ''}`}
        onDragOver={e => {
          e.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragLeave={e => {
          e.preventDefault();
          setIsDragOver(false);
        }}
        onDrop={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragOver(false);
          handleIncomingFiles(e.dataTransfer?.files);
        }}
      >
        {isCompact ? (
          <label
            htmlFor={fileInputId}
            className="attach-supporting-documents__compact-label mb-0 d-flex align-items-center"
          >
            <Paperclip size={18} className="text-info flex-shrink-0 mr-2" aria-hidden />
            <span className="font-weight-bold text-dark midFont mr-2">Attach Documents</span>
            <span className="badge badge-success">NEW</span>
          </label>
        ) : (
          <label htmlFor={fileInputId} className="attach-supporting-documents__label mb-0 pb-1">
            <CloudArrowUp size={18} className="text-muted mb-1" aria-hidden />
            <p className="small mb-0 text-dark">
              Drag files here or <span className="font-weight-bold text-info">browse</span>
            </p>
            <p className="xsmallFont mb-0 text-muted">PDF, JPG, PNG, DOCX, XLSX · 25MB max</p>
          </label>
        )}
      </div>

      {!isCompact && selectedFilesList}

      {isCompact && (
        <p className="attach-supporting-documents__compact-hint mb-0 small text-muted">
          PDF, JPG, PNG, DOCX, XLSX · 25MB max
        </p>
      )}
    </div>
  );
};

export default AttachSupportingDocuments;
